import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { noop, first, isEmpty, isNil, keys, cloneDeep } from 'lodash';
// Import Types
import type { DropdownOption, NFTVoucherCollectionItem, PartnerCurrency, TabConfig } from 'types';
// Import Store
import { useAppSelector } from 'store';
import { selectCurrencyRates } from 'store/slices/currencyRates';
import { selectUserSettingsCurrencyId } from 'store/slices/userData';
import { selectPartnerCurrencies } from 'store/slices/partnerSettings';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useMenuItem } from 'hooks/UseMenuItem';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import useHasPermission from 'hooks/UseHasPermission';
// Import UI Components
import {
    Modal,
    ExtendedInput,
    Button,
    DatePickerInput,
    BusyLoader,
    Alert,
    SkeletonLoader,
    Dropdown,
    Checkbox,
} from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import CustomTabs from 'components/common/CustomTabs';
import ValidatableDropdown from 'components/common/ValidatableDropdown';
import CurrencyAutoFillSection from 'components/common/CurrencyAutoFillSection';
// Import Services
import { AwardHttpService, UtilsHttpService } from 'services/http';
import { Helpers } from 'services';
// Import Enums
import { FormatNumberActionEnum, MenuItemIdEnum, PageTypesEnum } from 'enums';
// Import Constants
import { l, BonusTypesLabels, BonusLabels, AwardUsageTypeLabelValueList, PARTNER_SETTINGS } from 'constants/common';
import { datePickerConfigs, getStartDate, getEndDate } from './config';
// Import Styles
import styles from './AwardCEModal.module.scss';

const { getCurrencyLabel, customMomentWithoutTimezoneConversion, formatNumber } = Helpers;

// TODO: need rewrite props logic
const AwardCEModal = ({
    type,
    bonusType,
    openedModalData,
    getTableRefreshFn,
    setIsEditModalOpenedState,
    setIsCEAwardModalOpenedState,
}) => {
    const currencyId = useAppSelector(selectUserSettingsCurrencyId);
    const partnerCurrencies = useAppSelector(selectPartnerCurrencies);
    const totalCurrencyRates = useAppSelector(selectCurrencyRates);
    const { partnerDateFormat } = usePartnerDateFormats();

    const { hasPartnerSettings } = useHasPermission();
    const isAllowMultiWalletCashBonus = hasPartnerSettings(PARTNER_SETTINGS.IsAllowMultiWalletCashBonus);
    const isVisibleWallet =
        isAllowMultiWalletCashBonus &&
        ![
            BonusTypesLabels.LoyaltyPoint,
            BonusTypesLabels.FrenchFreeBet,
            BonusTypesLabels.WonderWheelSpin,
            BonusTypesLabels.NftVoucherBonus,
            BonusTypesLabels.PiePoints,
        ].includes(BonusTypesLabels[bonusType]);

    const currencyRates = useMemo(() => totalCurrencyRates?.[currencyId ?? ''], [totalCurrencyRates, currencyId]);
    const {
        convertedData: BalanceTypesData,
        isLoading: isLoadingBalanceTypesData,
        isLoadingConfig,
    } = useMenuItem(MenuItemIdEnum.BalanceTypes);
    const { data: currencyData, isLoading: isLoadingCurrencyData } = useMenuItem(MenuItemIdEnum.Currency);

    const partnerSupportedCurrencies = useMemo(() => {
        if (partnerCurrencies) {
            return partnerCurrencies.filter((item) => item.IsSupported);
        }
        return [];
    }, [partnerCurrencies]);

    const { data: wonderWheelsData, isLoading: isWonderWheelsDataLoading } = useMenuItem(
        BonusTypesLabels[bonusType] === BonusTypesLabels.WonderWheelSpin ? MenuItemIdEnum.WonderWheel : undefined,
    );

    const { t } = useTranslation();
    const { doPostRequest, doGetRequest } = useRequest();

    const [currencies, setCurrencies] = useState<Partial<PartnerCurrency>[]>([]);
    const [wallets, setWallets] = useState<AnyObject[]>([]);
    const [marketplaces, setMarketplaces] = useState([]);
    const [isLoadingMarketplaces, setIsLoadingMarketplaces] = useState(true);
    const [isLoadingNftVoucherCollections, setIsLoadingNftVoucherCollections] = useState(false);
    const [nftVoucherCollectionsList, setNftVoucherCollectionsList] = useState<
        Record<number, NFTVoucherCollectionItem>
    >({});
    const [nftVoucherDropdownData, setNftVoucherDropdownData] = useState<DropdownOption<number>[]>([]);

    const [date, setDate] = useState(
        type === PageTypesEnum.edit
            ? [openedModalData?.StartDate, openedModalData?.EndDate]
            : [datePickerConfigs.initStartDate, datePickerConfigs.initEndDate],
    );
    const [activeTabKey, setActiveTabKey] = useState('0');
    const [isSaveLoading, setIsSaveLoading] = useState(false);

    const {
        createAwardRequest,
        editAwardRequest,
        getNftMarketplacesRequest,
        getNftVoucherMarketplacesRequest,
        getNftVoucherCollectionsListRequest,
    } = useCancelableRequests(
        {
            createAwardRequest: AwardHttpService.createAward,
            editAwardRequest: AwardHttpService.editAward,
            getNftMarketplacesRequest: UtilsHttpService.getNftMarketplaces,
            getNftVoucherMarketplacesRequest: UtilsHttpService.getNftVoucherMarketplaces,
            getNftVoucherCollectionsListRequest: UtilsHttpService.getNftVoucherCollectionsList,
        },
        { takeLatest: ['getNftVoucherCollectionsListRequest'] },
    );

    const wonderWheelsDataDropdownModel = useMemo(
        () => wonderWheelsData.map(({ Name, Id }) => ({ label: `${Name} (${Id})`, value: Id })),
        [wonderWheelsData],
    );

    const usageTypeDropdownModel = useMemo(
        () => AwardUsageTypeLabelValueList.map(({ label, value }) => ({ label: t(label), value: value })),
        [t],
    );

    const formik = useFormik({
        initialValues: {
            inputValue: type === PageTypesEnum.edit ? openedModalData?.Name : '',
            currencyInputs: [],
            descriptionValue: type === PageTypesEnum.edit ? openedModalData?.Description : '',
            balanceType: type === PageTypesEnum.edit ? openedModalData?.BalanceType : null,
            ...(type === PageTypesEnum.edit &&
                ((BonusTypesLabels[bonusType] === BonusTypesLabels.PiePoints && {
                    usageType: openedModalData?.BonusDetails[0]?.Usage,
                    isLocalNetwork: openedModalData?.BonusDetails[0]?.IsLocalNetwork,
                }) ||
                    (BonusTypesLabels[bonusType] === BonusTypesLabels.NftVoucherBonus && {
                        marketplace: openedModalData?.BonusDetails[0]?.Marketplace,
                    }))),
        },
        onSubmit: noop,
        validationSchema: Yup.object({
            inputValue: Yup.string()
                .required(t(l.CannotBeEmpty, { fieldName: t(l.Name) }))
                .matches(/^[A-Za-z0-9]/, t(l.FirstCharacterCantBeASymbol, { fieldName: l.Value }))
                .min(3, t(l.MustContainMinimumCharacters, { fieldName: l.Value, count: 3 }))
                .max(50, t(l.MustContainMaximumCharacters, { fieldName: l.Value, count: 50 })),
            descriptionValue: Yup.string()
                .nullable()
                .max(250, t(l.MustContainMaximumCharacters, { fieldName: l.Value, count: 250 })),
            currencyInputs: Yup.array().of(
                Yup.object().shape({
                    min: Yup.string()
                        .required(t(l.CannotBeEmpty, { fieldName: t(l.Min) }))
                        .matches(/^\d{1,}(\.\d{0,9})?$/, t(l.MinCurrencyIsNotValid, { fieldName: l.Min }))
                        .test('min', t(l.MinMustBeSmallerThanMax, { label: t(l.Min) }), function (value) {
                            const { max } = this.parent;
                            return !value || !max || +value <= +max;
                        }),
                    max: Yup.string()
                        .required(t(l.CannotBeEmpty, { fieldName: t(l.Max) }))
                        .matches(/^\d{1,}(\.\d{0,9})?$/, t(l.MaxCurrencyIsNotValid, { fieldName: l.Max }))
                        .test('max', t(l.MaxMustBeBiggerThanMin, { label: t(l.Max) }), function (value) {
                            const { min } = this.parent;
                            return !value || !min || +value >= +min;
                        })
                        .test('max', t(l.MaxMustBeBiggerThanZero, { label: t(l.Max) }), (value: string) => {
                            return +value > 0;
                        }),
                }),
            ),
            nftFrom:
                BonusTypesLabels.NFTBonus === BonusTypesLabels[bonusType]
                    ? Yup.number()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.From) }))
                          .moreThan(0, t(l.MustBeBiggerThan, { fieldName: l.From, count: 0 }))
                          .test('nftFromTest', t(l.FromMustBeSmallerThanTo), function (value) {
                              const { nftTo } = this.parent;
                              return isNil(nftTo) || isNil(value) || +value < +nftTo;
                          })
                          .nullable()
                    : Yup.number().nullable(),
            nftTo:
                BonusTypesLabels.NFTBonus === BonusTypesLabels[bonusType]
                    ? Yup.number()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.To) }))
                          .moreThan(0, t(l.MustBeBiggerThan, { fieldName: l.To, count: 0 }))
                          .test('nftToTest', t(l.ToMustBeBiggerThanFrom), function (value) {
                              const { nftFrom } = this.parent;
                              return isNil(nftFrom) || isNil(value) || +value > +nftFrom;
                          })
                          .nullable()
                    : Yup.number().nullable(),
            balanceType: isVisibleWallet
                ? Yup.number()
                      .required(t(l.CannotBeEmpty, { fieldName: t(l.Wallet) }))
                      .nullable()
                : Yup.number().nullable(),
            marketplace: [BonusTypesLabels.NFTBonus, BonusTypesLabels.NftVoucherBonus].includes(
                BonusTypesLabels[bonusType],
            )
                ? Yup.number()
                      .required(t(l.CannotBeEmpty, { fieldName: t(l.Marketplace) }))
                      .nullable()
                : Yup.number().nullable(),
            wonderWheel:
                BonusTypesLabels[bonusType] === BonusTypesLabels.WonderWheelSpin
                    ? Yup.number()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.WonderWheelBonus) }))
                          .nullable()
                    : Yup.number().nullable(),
            nftVoucherCollectionId:
                BonusTypesLabels[bonusType] === BonusTypesLabels.NftVoucherBonus
                    ? Yup.number()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.NftVoucherCollection) }))
                          .nullable()
                    : Yup.number().nullable(),
            usageType:
                BonusTypesLabels[bonusType] === BonusTypesLabels.PiePoints
                    ? Yup.number()
                          .required(t(l.CannotBeEmpty, { fieldName: t(l.UsageType) }))
                          .nullable()
                    : Yup.number().nullable(),
        }),
    });

    const {
        values,
        errors,
        touched,
        setFieldValue,
        setValues,
        setFieldTouched,
        validateForm,
        submitForm,
        handleBlur,
        isValid,
    } = formik;
    const inputValueChangeHandler = (value) => {
        setFieldValue('inputValue', value, false)
            .then(() => {
                setFieldTouched('inputValue');
            })
            .then(() => submitForm());
    };

    const handleSave = () => {
        submitForm().then(() =>
            validateForm(values).then((formErrors) => {
                if (isEmpty(formErrors)) {
                    onOk();
                }
            }),
        );
    };

    const dateInputChangeHandler = (value) => {
        setDate([getStartDate(value[0]), getEndDate(value[1])]);
    };

    const descriptionValueChangeHandler = (value) => {
        setFieldValue('descriptionValue', value.target.value, false).then(() => submitForm());
    };

    const nftValuesChangeHandler = (e, fieldName) => {
        const inputValue = e.target.value;
        setFieldValue(fieldName, inputValue).then(() => submitForm());
    };

    function handleMinCurrencyChange(e, id) {
        const inputValue = e.target?.value.replace('-', '');
        const result = values.currencyInputs.map((item) => {
            if (item?.currency === id) {
                return { ...item, min: inputValue };
            }
            return { ...item };
        });
        setFieldValue('currencyInputs', result).then(() => submitForm());
    }

    function handleMaxCurrencyChange(e, id) {
        const inputValue = e.target?.value.replace('-', '');
        const result = values.currencyInputs.map((item) => {
            if (item?.currency === id) {
                return { ...item, max: inputValue };
            }
            return { ...item };
        });
        setFieldValue('currencyInputs', result).then(() => submitForm());
    }

    const dropdownChangeHandler = (e) => {
        setFieldValue('balanceType', e?.value ?? '').then(() => submitForm());
    };

    const fetchCollectionData = (marketplaceId) => {
        setIsLoadingNftVoucherCollections(true);
        doGetRequest(getNftVoucherCollectionsListRequest.request, {
            queryString: { marketplace: marketplaceId },
            successCallback: (data) => {
                setNftVoucherCollectionsList(
                    data.reduce((acc, val) => {
                        acc[val.CollectionId] = val;
                        return acc;
                    }, {}),
                );
                setNftVoucherDropdownData(
                    data.map((item) => ({ label: `${item.Name}(${item.CollectionId})`, value: item.CollectionId })),
                );
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsLoadingNftVoucherCollections(false);
            }
        });
    };

    const marketplaceDropdownChangeHandler = (e) => {
        const newMarketplaceId = e?.value ?? null;

        if (newMarketplaceId === values.marketplace) return;

        setFieldValue('marketplace', newMarketplaceId).then(() => submitForm());

        if (BonusTypesLabels[bonusType] === BonusTypesLabels.NftVoucherBonus) {
            setFieldValue('nftVoucherCollectionId', '', false).then(() => {
                setFieldTouched('nftVoucherCollectionId');
            });

            if (!isNil(newMarketplaceId)) {
                fetchCollectionData(newMarketplaceId);
            }
        }
    };

    const usageTypeDropdownChangeHandler = (e) => {
        setFieldValue('usageType', e?.value ?? '').then(() => submitForm());
    };

    const cancelHandler = () => {
        setFieldValue('inputValue', '', false).then(() => {
            setFieldTouched('inputValue');
        });
        setFieldValue('descriptionValue', '', false);
        setIsCEAwardModalOpenedState(false);
        setIsEditModalOpenedState(false);
    };

    const getMinMaxErrorMessages = (field) => {
        return keys(errors).includes('currencyInputs') ? errors.currencyInputs?.[activeTabKey]?.[field] : '';
    };

    const getMinMaxTuoched = (field) => {
        return keys(touched).includes('currencyInputs') ? touched.currencyInputs?.[activeTabKey]?.[field] : '';
    };

    const getNftErrorMessages = (field) => {
        return errors[field] ?? '';
    };

    const tabChangeHandler = (tabKey) => {
        setActiveTabKey(tabKey);
    };

    const getEditModalCurrencyInputs = () => {
        if (type === PageTypesEnum.edit) {
            if (
                [
                    BonusTypesLabels.LoyaltyPoint,
                    BonusTypesLabels.WonderWheelSpin,
                    BonusTypesLabels.NftVoucherBonus,
                    BonusTypesLabels.PiePoints,
                ].includes(BonusTypesLabels[bonusType])
            ) {
                return openedModalData.BonusDetails.map((item) => {
                    if ([BonusTypesLabels.WonderWheelSpin].includes(BonusTypesLabels[bonusType])) {
                        setFieldValue('wonderWheel', item.Id.toString()).then(() => validateForm());
                    }
                    if ([BonusTypesLabels.NftVoucherBonus].includes(BonusTypesLabels[bonusType])) {
                        setFieldValue('nftVoucherCollectionId', item.Id).then(() => validateForm());
                    }
                    return {
                        currency: item.CurrencyCode,
                        min: +item.MinAmount,
                        max: +item.MaxAmount,
                        isValid: true,
                    };
                });
            } else {
                const openedModalDataBonusDetails = openedModalData.BonusDetails.reduce((acc, val) => {
                    acc[val.CurrencyCode] = val;
                    return acc;
                }, {});

                return partnerSupportedCurrencies.map((item) => {
                    const tmpVal = openedModalDataBonusDetails[item.CurrencyId] ?? null;
                    return {
                        currency: tmpVal?.CurrencyCode ?? item?.CurrencyId,
                        min: tmpVal?.MinAmount ? +tmpVal?.MinAmount : '',
                        max: tmpVal?.MaxAmount ? +tmpVal?.MaxAmount : '',
                        isValid: !isNil(tmpVal),
                    };
                });
            }
        } else {
            return [];
        }
    };

    useEffect(() => {
        if (!isLoadingBalanceTypesData && !isLoadingConfig) {
            setWallets(cloneDeep(BalanceTypesData));
        }
    }, [isLoadingBalanceTypesData, isLoadingConfig, BalanceTypesData]);

    const init = () => {
        if (BonusTypesLabels.NFTBonus !== BonusTypesLabels[bonusType]) {
            let currenciesData: Partial<PartnerCurrency>[] = [];
            if (BonusTypesLabels.LoyaltyPoint === BonusTypesLabels[bonusType]) {
                currenciesData = [{ CurrencyId: l.LP }];
            } else if (BonusTypesLabels.WonderWheelSpin === BonusTypesLabels[bonusType]) {
                currenciesData = [{ CurrencyId: l.SPIN }];
            } else if (BonusTypesLabels.NftVoucherBonus === BonusTypesLabels[bonusType]) {
                currenciesData = [{ CurrencyId: l.NFTV }];
            } else if (BonusTypesLabels.PiePoints === BonusTypesLabels[bonusType]) {
                currenciesData = [{ CurrencyId: l.BPIEP }];
            } else {
                if (!isNil(partnerSupportedCurrencies)) {
                    currenciesData = partnerSupportedCurrencies;
                }
            }

            setCurrencies(currenciesData);
            if (type === PageTypesEnum.edit) {
                setFieldValue('currencyInputs', getEditModalCurrencyInputs());
            } else {
                setFieldValue(
                    'currencyInputs',
                    currenciesData.map(({ CurrencyId }) => {
                        return { currency: CurrencyId, min: '', max: '', isValid: false };
                    }),
                ).then(() => {
                    submitForm();
                });
            }
        }
    };

    const getIsValidCurrencyInputsValues = () => {
        const data = values.currencyInputs;
        if (keys(errors).includes('currencyInputs')) {
            data.forEach((item, index) => {
                if (keys(errors.currencyInputs).includes(`${index}`)) {
                    if (isNil(errors.currencyInputs?.[index])) {
                        item.isValid = true;
                    } else {
                        item.isValid = false;
                    }
                } else {
                    item.isValid = true;
                }
            });
        } else {
            data.forEach((item) => {
                item.isValid = true;
            });
        }
        return data;
    };

    const setCurrencyInputsValidation = () => {
        setFieldValue('currencyInputs', getIsValidCurrencyInputsValues(), false);
    };

    const isValidTabs = () => {
        return getIsValidCurrencyInputsValues().map((item) => {
            return item.isValid;
        });
    };

    const onOk = () => {
        setIsSaveLoading(true);
        const requestBody: AnyObject = {
            Name: values?.inputValue,
            Description: values?.descriptionValue,
            StartDate: date[0],
            EndDate: date[1],
            Type: BonusTypesLabels[bonusType],
            BalanceType: values?.balanceType ?? null,
        };

        if (type === PageTypesEnum.edit) {
            requestBody.Id = openedModalData?.Id;
        }

        if (BonusTypesLabels.NFTBonus !== BonusTypesLabels[bonusType]) {
            requestBody.BonusDetails = values?.currencyInputs?.map((item) => {
                return {
                    CurrencyCode: item.currency,
                    MinAmount: item.min,
                    MaxAmount: item.max,
                    ...(BonusTypesLabels.WonderWheelSpin === BonusTypesLabels[bonusType] && { Id: values.wonderWheel }),
                    ...(BonusTypesLabels.NftVoucherBonus === BonusTypesLabels[bonusType] && {
                        Id: values.nftVoucherCollectionId,
                    }),
                    ...(BonusTypesLabels.PiePoints === BonusTypesLabels[bonusType] && {
                        Usage: values.usageType,
                        IsLocalNetwork: !!values.isLocalNetwork,
                        Id: 0,
                        PartnerBonusId: 0,
                    }),
                    ...(BonusTypesLabels.NftVoucherBonus === BonusTypesLabels[bonusType] && {
                        Marketplace: values.marketplace,
                    }),
                };
            });
        } else {
            requestBody.BonusDetails = [
                {
                    From: values?.nftFrom,
                    To: values?.nftTo,
                    Marketplace: values?.marketplace,
                },
            ];
        }

        doPostRequest((type === PageTypesEnum.edit ? editAwardRequest : createAwardRequest).request, {
            requestBody: requestBody,
            successCallback: () => {
                type === PageTypesEnum.edit ? setIsEditModalOpenedState(false) : setIsCEAwardModalOpenedState(false);
                getTableRefreshFn();
            },
        }).then(() => {
            setIsSaveLoading(false);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(setCurrencyInputsValidation, [
        // eslint-disable-next-line react-hooks/exhaustive-deps
        values?.currencyInputs[activeTabKey]?.min,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        values?.currencyInputs[activeTabKey]?.max,
    ]);

    useEffect(() => {
        const bonusDetails: AnyObject = first(openedModalData?.BonusDetails ?? []);
        if (BonusTypesLabels.NFTBonus === BonusTypesLabels[bonusType]) {
            setValues((prev) => ({ ...prev, nftFrom: bonusDetails?.From, nftTo: bonusDetails?.To }));
            doGetRequest(getNftMarketplacesRequest.request, {
                successCallback: (data) => {
                    setMarketplaces((data ?? []).map((item) => ({ value: item.Value, label: item.DisplayName })));
                    setFieldValue('marketplace', bonusDetails?.Marketplace);
                    submitForm();
                },
            }).then(() => setIsLoadingMarketplaces(false));
        }
        if (BonusTypesLabels.NftVoucherBonus === BonusTypesLabels[bonusType]) {
            doGetRequest(getNftVoucherMarketplacesRequest.request, {
                successCallback: (data) => {
                    setMarketplaces((data ?? []).map((item) => ({ value: item.Value, label: item.DisplayName })));
                    setFieldValue('marketplace', bonusDetails?.Marketplace);
                    submitForm();
                },
            }).then(() => setIsLoadingMarketplaces(false));

            if (!isNil(bonusDetails?.Marketplace)) {
                fetchCollectionData(bonusDetails?.Marketplace);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [partnerSupportedCurrencies]);

    const minMaxLabels = useMemo(() => {
        return [
            BonusLabels.NftVoucherBonus,
            BonusLabels.WonderWheelSpin,
            BonusLabels.FreeSpin,
            BonusLabels.LoyaltyPoint,
        ].includes(bonusType)
            ? { min: l.MinCount, max: l.MaxCount }
            : { min: l.Min, max: l.Max };
    }, [bonusType]);

    const getTabContent = (id) => {
        const currencyValue = values.currencyInputs.find((item) => item.currency === id);
        return (
            <div className="currency-min-max-section">
                <div className="min-max-currency">
                    <ExtendedInput
                        autocomplete="off"
                        value={currencyValue?.min}
                        onBlur={handleBlur}
                        onChange={(e) => handleMinCurrencyChange(e, id)}
                        type="number"
                        name="min"
                        placeholder={t(minMaxLabels.min)}
                        label={getCurrencyLabel(
                            t(minMaxLabels.min),
                            currencyValue?.min,
                            currencyValue?.currency,
                            currencyId,
                            currencyRates,
                        )}
                        labelAppearance="swap"
                        isValid={!(getMinMaxTuoched('min') && getMinMaxErrorMessages('min'))}
                        errorText={getMinMaxTuoched('min') && getMinMaxErrorMessages('min')}
                    />
                    <ExtendedInput
                        autocomplete="off"
                        value={currencyValue?.max}
                        onBlur={handleBlur}
                        onChange={(e) => handleMaxCurrencyChange(e, id)}
                        type="number"
                        name="max"
                        placeholder={t(minMaxLabels.max)}
                        label={getCurrencyLabel(
                            t(minMaxLabels.max),
                            currencyValue?.max,
                            currencyValue?.currency,
                            currencyId,
                            currencyRates,
                        )}
                        labelAppearance="swap"
                        isValid={!(getMinMaxTuoched('max') && getMinMaxErrorMessages('max'))}
                        errorText={getMinMaxTuoched('max') && getMinMaxErrorMessages('max')}
                    />
                </div>
            </div>
        );
    };

    const tabsConfig: TabConfig[] = currencies.map((item, index) => {
        return {
            tabTitle: item?.CurrencyId,
            tabKey: `${index}`,
            tabContent: getTabContent(item?.CurrencyId),
            permissions: [],
        };
    });

    const onAutoFillClickHandler = (data) => {
        const { autoFillMinValue, autoFillMaxValue, selectedCurrency } = data;
        const result = values.currencyInputs.map((currency) => {
            const tmpCurrency = { ...currency };
            const tmpMin = (totalCurrencyRates?.[tmpCurrency.currency][selectedCurrency].Rate ?? 0) * +autoFillMinValue;
            const tmpMax = (totalCurrencyRates?.[tmpCurrency.currency][selectedCurrency].Rate ?? 0) * +autoFillMaxValue;
            tmpCurrency.min = formatNumber(tmpMin, {
                type: FormatNumberActionEnum.CleanNumber,
                minPrecision: 9,
                maxPrecision: 9,
            });
            tmpCurrency.max = formatNumber(tmpMax, { type: FormatNumberActionEnum.CleanNumber, precision: 9 });
            return tmpCurrency;
        });
        setFieldValue('currencyInputs', result).then(() => submitForm());
    };

    const currenciesDropdownData = useMemo(() => {
        return currencyData.map(({ Id, Name }) => ({ label: Name, value: Id }));
    }, [currencyData]);

    const isAutoFillSectionVisible = useMemo(
        () =>
            [BonusTypesLabels.CorrectionBonus, BonusTypesLabels.CashBonus, BonusTypesLabels.FrenchFreeBet].includes(
                BonusTypesLabels[bonusType],
            ),
        [bonusType],
    );

    return (
        <Modal
            withPortal={true}
            background="dark-background"
            position="center"
            size="content-size"
            className="configuration-create-award-modal"
            title={t(l[`${type.charAt(0).toUpperCase() + type.slice(1)}AwardingTitle`], {
                bonusType: type === PageTypesEnum.edit ? t(BonusTypesLabels[openedModalData?.Type]) : t(bonusType),
            })}
            closable={false}
            closeOnClickOutside={false}
            visible={true}
            onCancel={cancelHandler}
            footer={
                <>
                    <Button color="default" appearance="minimal" onClick={cancelHandler}>
                        {t(l.Cancel)}
                    </Button>
                    <ButtonWithLoader
                        color="primary"
                        onClick={handleSave}
                        isLoading={isSaveLoading}
                        isDisabled={!isValid}
                    >
                        {t(l.Save)}
                    </ButtonWithLoader>
                </>
            }
        >
            {BonusTypesLabels.FrenchFreeBet === BonusTypesLabels[bonusType] && (
                <div className="french-free-bet-alert-holder">
                    <Alert
                        closable={false}
                        title={t(l.FrenchFreeBetInfoMessage)}
                        iconProps={{ type: 'bc-icon-info' }}
                    />
                </div>
            )}
            <div className="awarding-chanel-row">
                <ExtendedInput
                    placeholder={t(l.Name)}
                    value={values.inputValue}
                    onChange={(e) => inputValueChangeHandler(e.target.value)}
                    onBlur={handleBlur}
                    isValid={!(touched.inputValue && errors.inputValue)}
                    errorText={touched.inputValue && errors.inputValue}
                    labelAppearance="swap"
                />
                <DatePickerInput.WithRange
                    onChange={dateInputChangeHandler}
                    value={date}
                    min={datePickerConfigs.min}
                    max={datePickerConfigs.max}
                    label={t(l.BonusProgramPeriod)}
                    labelAppearance="swap"
                />
            </div>

            {BonusTypesLabels.WonderWheelSpin === BonusTypesLabels[bonusType] && (
                <div style={{ display: 'flex', width: '100%', gap: '30px', marginBottom: '30px', marginTop: '30px' }}>
                    <Dropdown
                        label={t(l.WonderWheelBonus)}
                        placeholder={t(l.WonderWheelBonus)}
                        labelAppearance="swap"
                        data={wonderWheelsDataDropdownModel}
                        value={values?.wonderWheel}
                        isLoading={isWonderWheelsDataLoading}
                        onChange={(e) => setFieldValue('wonderWheel', e.value)}
                        isValid={!errors.wonderWheel}
                        errorText={errors.wonderWheel}
                    />
                </div>
            )}
            <div className="award-description">
                <ExtendedInput
                    labelAppearance="swap"
                    type="textarea"
                    inputSize="small"
                    placeholder={t(l.Description)}
                    value={values.descriptionValue}
                    onChange={descriptionValueChangeHandler}
                    isValid={!(touched.descriptionValue && errors.descriptionValue)}
                    errorText={touched.descriptionValue && errors.descriptionValue}
                />
            </div>

            {[BonusTypesLabels.NFTBonus, BonusTypesLabels.NftVoucherBonus].includes(BonusTypesLabels[bonusType]) && (
                <>
                    <div className="title-wrapper">
                        <p className="title-wrapper-text">{t(l.Marketplace)}</p>
                        <span className="title-wrapper-line" />
                    </div>
                    <div className="crm-marketplace-dropdown">
                        <SkeletonLoader isBusy={isLoadingMarketplaces}>
                            {!isLoadingMarketplaces && (
                                <ValidatableDropdown
                                    isMultiSelect={false}
                                    disabled={false}
                                    hasSearch={false}
                                    inputSize="default"
                                    placeholder={t(l.Marketplace)}
                                    appearance="outline"
                                    data={marketplaces}
                                    searchPlaceholderText={t(l.Search)}
                                    labelAppearance="swap"
                                    noDataText={t(l.NoDataFound)}
                                    onChange={marketplaceDropdownChangeHandler}
                                    isValid={!errors.marketplace}
                                    errorText={errors.marketplace}
                                    value={values?.marketplace}
                                    clearable={true}
                                />
                            )}
                        </SkeletonLoader>
                    </div>
                    {BonusTypesLabels.NftVoucherBonus === BonusTypesLabels[bonusType] && (
                        <div className={styles.container}>
                            <Dropdown
                                data={nftVoucherDropdownData}
                                label={t(l.NftVoucherCollection)}
                                placeholder={t(l.NftVoucherCollection)}
                                labelAppearance="swap"
                                value={values?.nftVoucherCollectionId}
                                isLoading={isLoadingNftVoucherCollections}
                                onChange={(e) => setFieldValue('nftVoucherCollectionId', e.value)}
                                isValid={isNil(values?.marketplace) || !errors.nftVoucherCollectionId}
                                errorText={errors.nftVoucherCollectionId}
                                disabled={isNil(values?.marketplace)}
                            />
                            {values.nftVoucherCollectionId && !isEmpty(nftVoucherCollectionsList) && (
                                <div className="award-key-value-wrapper">
                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.Price)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {nftVoucherCollectionsList[values.nftVoucherCollectionId].Price}
                                        </span>
                                    </div>

                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.Slug)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {nftVoucherCollectionsList[values.nftVoucherCollectionId].Slug}
                                        </span>
                                    </div>
                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.Currency)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {nftVoucherCollectionsList[values.nftVoucherCollectionId].Currency}
                                        </span>
                                    </div>
                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.Count)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {nftVoucherCollectionsList[values.nftVoucherCollectionId].Count}
                                        </span>
                                    </div>
                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.RewardDate)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {customMomentWithoutTimezoneConversion(
                                                nftVoucherCollectionsList[values.nftVoucherCollectionId].RewardDate,
                                            ).format(partnerDateFormat)}
                                        </span>
                                    </div>
                                    <div className="key-value-h">
                                        <span className="ellipsis-text kv-label">{t(l.ExpirationDate)}</span>
                                        <span className="ellipsis-text kv-value kv-value-css">
                                            {isNil(nftVoucherCollectionsList[values.nftVoucherCollectionId].ExpireDate)
                                                ? t(l.NA)
                                                : customMomentWithoutTimezoneConversion(
                                                      nftVoucherCollectionsList[values.nftVoucherCollectionId]
                                                          .ExpireDate,
                                                  ).format(partnerDateFormat)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {BonusTypesLabels.NFTBonus === BonusTypesLabels[bonusType] && (
                        <>
                            <div className="title-wrapper">
                                <p className="title-wrapper-text">{t(l.Settings)}</p>
                                <span className="title-wrapper-line" />
                            </div>
                            <div className="nft-bonus-setting-container">
                                <ExtendedInput
                                    autocomplete="off"
                                    value={values?.nftFrom}
                                    onChange={(e) => nftValuesChangeHandler(e, 'nftFrom')}
                                    type="number"
                                    name="from"
                                    placeholder={t(l.From)}
                                    labelAppearance="swap"
                                    isValid={!getNftErrorMessages('nftFrom')}
                                    errorText={getNftErrorMessages('nftFrom')}
                                />

                                <ExtendedInput
                                    autocomplete="off"
                                    value={values?.nftTo}
                                    onChange={(e) => nftValuesChangeHandler(e, 'nftTo')}
                                    type="number"
                                    name="to"
                                    placeholder={t(l.To)}
                                    labelAppearance="swap"
                                    isValid={!getNftErrorMessages('nftTo')}
                                    errorText={getNftErrorMessages('nftTo')}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
            {BonusTypesLabels.NFTBonus !== BonusTypesLabels[bonusType] && (
                <>
                    {isVisibleWallet && (
                        <>
                            <div className="title-wrapper">
                                <p className="title-wrapper-text">{t(l.Wallet)}</p>
                                <span className="title-wrapper-line" />
                            </div>
                            <SkeletonLoader isBusy={isLoadingBalanceTypesData}>
                                {!isLoadingBalanceTypesData && (
                                    <ValidatableDropdown
                                        isMultiSelect={false}
                                        disabled={false}
                                        hasSearch={true}
                                        inputSize="default"
                                        placeholder={t(l.Wallet)}
                                        appearance="outline"
                                        data={wallets}
                                        searchPlaceholderText={t(l.Search)}
                                        labelAppearance="swap"
                                        noDataText={t(l.NoDataFound)}
                                        onChange={dropdownChangeHandler}
                                        isValid={!errors.balanceType}
                                        errorText={errors.balanceType}
                                        value={values?.balanceType}
                                        clearable={true}
                                        wrapperClassName="crm-wallets-dropdown"
                                    />
                                )}
                            </SkeletonLoader>
                            <Alert
                                closable={false}
                                title={t(l.AvailableBonusTypeAlert)}
                                iconProps={{ type: 'bc-icon-info' }}
                            />
                        </>
                    )}
                    <div className="title-wrapper">
                        <p className="title-wrapper-text">
                            {[BonusTypesLabels.LoyaltyPoint, BonusTypesLabels.PiePoints].includes(
                                BonusTypesLabels[bonusType],
                            )
                                ? t(l.Settings)
                                : t(l.SettingsPerCurrency)}
                        </p>
                        <span className="title-wrapper-line" />
                    </div>
                    {isAutoFillSectionVisible && (
                        <>
                            <CurrencyAutoFillSection
                                dropdownData={currenciesDropdownData}
                                onOk={onAutoFillClickHandler}
                                alertMessage={t(l.CurrencyAutoFillInfoAlertCreate)}
                                withMinMax={true}
                            />
                        </>
                    )}
                    {BonusTypesLabels.PiePoints === BonusTypesLabels[bonusType] && (
                        <div className="pi-points-award-settings-wrapper">
                            <Dropdown
                                label={t(l.UsageType)}
                                placeholder={t(l.UsageType)}
                                labelAppearance="swap"
                                data={usageTypeDropdownModel}
                                value={values?.usageType}
                                onChange={usageTypeDropdownChangeHandler}
                                isValid={!errors.usageType}
                                errorText={errors.usageType}
                            />
                            <Checkbox
                                label={t(l.IsLocalNetwork)}
                                checked={values?.isLocalNetwork}
                                onChange={(e) => setFieldValue('isLocalNetwork', e.target.checked)}
                            />
                        </div>
                    )}
                    <BusyLoader
                        isBusy={
                            ![BonusTypesLabels.LoyaltyPoint, BonusTypesLabels.WonderWheelSpin].includes(
                                BonusTypesLabels[bonusType],
                            ) && isLoadingCurrencyData
                        }
                        type="spinner"
                        spinnerSize="medium"
                    >
                        <div className="currency-tabs">
                            <CustomTabs
                                tabsConfig={tabsConfig}
                                activeTabKey={activeTabKey}
                                tabChangeHandler={tabChangeHandler}
                                isValid={isValidTabs()}
                            />
                        </div>
                    </BusyLoader>
                </>
            )}
        </Modal>
    );
};

AwardCEModal.propTypes = {
    type: PropTypes.string.isRequired,
    bonusType: PropTypes.string.isRequired,
    getTableRefreshFn: PropTypes.func.isRequired,
    setIsEditModalOpenedState: PropTypes.func.isRequired,
    setIsCEAwardModalOpenedState: PropTypes.func.isRequired,
    openedModalData: PropTypes.any,
};
AwardCEModal.defaultProps = {
    openedModalData: {},
    setIsEditModalOpenedState: noop,
};
export default memo(AwardCEModal);
